import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2, RefreshCw, Eye, Users, CalendarDays, CalendarRange, UserPlus,
  BadgeCheck, FileText, CheckCircle2, Percent, ArrowLeft, TrendingUp,
  Globe, Monitor, Smartphone, Tablet, Link2, Megaphone, MousePointerClick,
  ShieldOff, Filter, ShieldCheck, Radio,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, Legend,
} from "recharts";
import { AppShell } from "@/components/app/AppShell";
import { useAuth } from "@/context/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import api, { getErrorMessage } from "@/lib/api";
import { LiveVisitors } from "@/components/admin/LiveVisitors";

const REFRESH_MS = 60000;

const StatCard = ({ icon: Icon, label, value, sub, accent = "text-primary", testid }) => (
  <div className="rounded-xl border bg-card p-5" data-testid={testid}>
    <div className="flex items-center justify-between">
      <p className="text-sm text-muted-foreground">{label}</p>
      <Icon className={`h-4 w-4 ${accent}`} />
    </div>
    <p className="mt-2 font-display text-2xl font-semibold" data-testid={`${testid}-value`}>{value}</p>
    {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
  </div>
);

const SectionTitle = ({ children, icon: Icon }) => (
  <h2 className="mb-3 mt-8 flex items-center gap-2 font-display text-lg font-semibold first:mt-0">
    {Icon && <Icon className="h-4 w-4 text-primary" />}{children}
  </h2>
);

const Card = ({ children, className = "", testid }) => (
  <div className={`rounded-xl border bg-card p-5 ${className}`} data-testid={testid}>{children}</div>
);

// Horizontal distribution bar (device / browser breakdowns)
const DistRow = ({ label, count, total, icon: Icon, testid }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div data-testid={testid}>
      <div className="mb-1 flex items-center justify-between text-xs">
        <span className="flex items-center gap-1.5 text-muted-foreground">
          {Icon && <Icon className="h-3.5 w-3.5" />}{label}
        </span>
        <span className="font-medium tabular-nums">{count} · {pct}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-muted">
        <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const fmtDay = (d) => {
  // d = "YYYY-MM-DD" -> "12 Jun"
  try {
    const dt = new Date(`${d}T00:00:00`);
    return dt.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  } catch (e) { return d; }
};

const FunnelStep = ({ label, value, rate, last, testid }) => (
  <div className="flex items-center gap-3" data-testid={testid}>
    <div className="flex-1 rounded-lg border bg-secondary/40 px-4 py-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{label}</span>
        <span className="font-display text-lg font-semibold tabular-nums">{value}</span>
      </div>
    </div>
    {!last && (
      <div className="flex w-16 shrink-0 flex-col items-center text-xs text-muted-foreground">
        <span className="font-medium text-primary">{Math.min(rate, 100)}%</span>
        <span>→</span>
      </div>
    )}
  </div>
);

export default function AdminAnalytics() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!authLoading && user && !user.is_admin) navigate("/app/dashboard", { replace: true });
  }, [authLoading, user, navigate]);

  const load = useCallback(async (manual = false) => {
    if (manual) setRefreshing(true);
    try {
      const r = await api.get("/admin/analytics");
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(getErrorMessage(e, "Failed to load analytics."));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (user?.is_admin) {
      load();
      const id = setInterval(() => load(), REFRESH_MS);
      return () => clearInterval(id);
    }
  }, [user, load]);

  if (authLoading || (user && !user.is_admin)) {
    return (
      <AppShell title="Analytics">
        <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      </AppShell>
    );
  }

  const v = data?.visitors;
  const u = data?.users;
  const a = data?.assessments;
  const c = data?.conversion;
  const ts = (data?.timeseries_30d || []).map((d) => ({ ...d, label: fmtDay(d.day) }));
  const topPages = data?.top_pages || [];
  const sources = data?.sources || [];
  const referrers = data?.referrers || [];
  const campaigns = data?.campaigns || [];
  const devices = data?.devices || {};
  const browsers = data?.browsers || [];
  const funnel = data?.funnel;
  const internal = data?.internal_excluded || {};
  const verification = data?.verification || {};

  const deviceTotal = (devices.desktop || 0) + (devices.mobile || 0) + (devices.tablet || 0) + (devices.unknown || 0);
  const browserTotal = browsers.reduce((s, b) => s + b.count, 0);

  return (
    <AppShell
      title="Website Analytics"
      action={
        <button
          onClick={() => load(true)}
          className="inline-flex items-center gap-1.5 rounded-lg border bg-card px-3 py-1.5 text-sm font-medium text-foreground hover:bg-accent"
          data-testid="analytics-refresh"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh
        </button>
      }
    >
      <div className="space-y-2" data-testid="admin-analytics">
        <button
          onClick={() => navigate("/app/admin")}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          data-testid="analytics-back"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Admin
        </button>
        <div className="flex flex-wrap items-center gap-2">
          <TrendingUp className="h-5 w-5 text-primary" />
          <span className="text-sm text-muted-foreground">
            First-party website analytics. Auto-refreshes every 60s.
          </span>
          {data?.stripe_live && <Badge className="bg-[hsl(var(--risk-low-bg))] text-[hsl(var(--risk-low))]">Stripe live</Badge>}
        </div>

        {error && (
          <div className="rounded-lg border border-[hsl(var(--risk-high))]/25 bg-[hsl(var(--risk-high-bg))] p-3 text-sm text-[hsl(var(--risk-high))]" data-testid="analytics-error">{error}</div>
        )}

        {loading ? (
          <div className="grid grid-cols-2 gap-4 pt-4 sm:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
          </div>
        ) : (
          <>
            {/* LIVE VISITORS (real-time) */}
            <SectionTitle icon={Radio}>Live visitors</SectionTitle>
            <LiveVisitors />

            {/* VISITORS */}
            <SectionTitle icon={Eye}>Visitors</SectionTitle>
            <div className="mb-1 flex items-center gap-1.5 text-xs text-[hsl(var(--risk-low))]">
              <ShieldCheck className="h-3.5 w-3.5" />
              <span>Genuine public traffic only — your own admin, testing, preview &amp; internal-page activity is excluded.</span>
            </div>
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <StatCard icon={Eye} label="Total page views" value={v.total} sub="public only" testid="an-visits-total" />
              <StatCard icon={Users} label="Unique visitors" value={v.unique} sub="public only" testid="an-visits-unique" />
              <StatCard icon={CalendarDays} label="Visitors today" value={v.today} accent="text-[hsl(var(--risk-low))]" testid="an-visits-today" />
              <StatCard icon={CalendarRange} label="Visitors this month" value={v.this_month} testid="an-visits-month" />
            </div>

            {/* VERIFICATION — public vs internal today */}
            <SectionTitle icon={Filter}>Traffic verification (today)</SectionTitle>
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <StatCard icon={Users} label="Public visitors today" value={verification.public_visitors_today ?? 0} accent="text-[hsl(var(--risk-low))]" testid="an-verify-public-visitors" />
              <StatCard icon={ShieldOff} label="Internal visitors excluded today" value={verification.internal_visitors_excluded_today ?? 0} accent="text-muted-foreground" testid="an-verify-internal-visitors" />
              <StatCard icon={Eye} label="Public page views today" value={verification.public_page_views_today ?? 0} accent="text-[hsl(var(--risk-low))]" testid="an-verify-public-views" />
              <StatCard icon={ShieldOff} label="Internal page views excluded today" value={verification.internal_page_views_excluded_today ?? 0} accent="text-muted-foreground" testid="an-verify-internal-views" />
            </div>

            {/* INTERNAL TRAFFIC EXCLUDED (all-time) */}
            <SectionTitle icon={ShieldOff}>Internal traffic excluded</SectionTitle>
            <Card testid="an-internal-excluded">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <p className="text-sm text-muted-foreground">Internal page views filtered</p>
                  <p className="mt-1 font-display text-2xl font-semibold tabular-nums" data-testid="an-internal-pageviews">{internal.page_views_total ?? 0}</p>
                  <p className="text-xs text-muted-foreground">{internal.page_views_today ?? 0} today</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Internal visitors filtered</p>
                  <p className="mt-1 font-display text-2xl font-semibold tabular-nums" data-testid="an-internal-visitors">{internal.visitors_total ?? 0}</p>
                  <p className="text-xs text-muted-foreground">{internal.visitors_today ?? 0} today</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Last exclusion</p>
                  <p className="mt-1 font-display text-base font-semibold" data-testid="an-internal-last">
                    {internal.last_exclusion_at ? new Date(internal.last_exclusion_at).toLocaleString() : "—"}
                  </p>
                  <p className="text-xs text-muted-foreground">admin · internal routes · preview/localhost</p>
                </div>
              </div>
            </Card>

            {/* TRAFFIC OVER TIME */}
            <SectionTitle icon={TrendingUp}>Traffic over time</SectionTitle>
            <Card testid="an-timeseries">
              <p className="text-xs text-muted-foreground">Daily page views &amp; unique visitors — last 30 days</p>
              <div className="mt-4 h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={ts} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                    <defs>
                      <linearGradient id="pv" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.28} />
                        <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="uv" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(var(--risk-low))" stopOpacity={0.22} />
                        <stop offset="100%" stopColor="hsl(var(--risk-low))" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={11} interval={4} />
                    <YAxis allowDecimals={false} tickLine={false} axisLine={false} fontSize={11} />
                    <RTooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", fontSize: 13 }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Area type="monotone" name="Page views" dataKey="pageviews" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#pv)" />
                    <Area type="monotone" name="Unique visitors" dataKey="unique_visitors" stroke="hsl(var(--risk-low))" strokeWidth={2} fill="url(#uv)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Card>

            {/* CONVERSION FUNNEL */}
            <SectionTitle icon={MousePointerClick}>Conversion funnel</SectionTitle>
            <Card testid="an-funnel">
              {funnel ? (
                <div className="space-y-3">
                  <FunnelStep label="Pricing viewed" value={funnel.pricing_viewed} rate={funnel.rates.pricing_to_signup_started} testid="an-funnel-pricing" />
                  <FunnelStep label="Signup started" value={funnel.signup_started} rate={funnel.rates.signup_started_to_signup} testid="an-funnel-signup-started" />
                  <FunnelStep label="Signups" value={funnel.signups} rate={funnel.rates.signup_to_checkout} testid="an-funnel-signups" />
                  <FunnelStep label="Checkout started" value={funnel.checkout_started} rate={funnel.rates.checkout_to_paid} testid="an-funnel-checkout" />
                  <FunnelStep label="Paid subscribers" value={funnel.paid} rate={0} last testid="an-funnel-paid" />
                  <p className="pt-2 text-xs text-muted-foreground">
                    Assessments: {funnel.assessments_started} started → {funnel.assessments_completed} completed
                    ({funnel.rates.assessment_start_to_complete}% completion).
                    Event-based metrics are tracked going forward.
                  </p>
                </div>
              ) : <p className="text-sm text-muted-foreground">No funnel data yet.</p>}
            </Card>

            {/* TOP PAGES + SOURCES */}
            <div className="grid gap-4 lg:grid-cols-2">
              <div>
                <SectionTitle icon={FileText}>Top pages</SectionTitle>
                <Card className="p-0 overflow-hidden" testid="an-top-pages">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Path</TableHead>
                        <TableHead className="text-right">Views</TableHead>
                        <TableHead className="text-right">Unique</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {topPages.map((p) => (
                        <TableRow key={p.path} data-testid={`an-page-${p.path}`}>
                          <TableCell className="max-w-[220px] truncate font-medium">{p.path}</TableCell>
                          <TableCell className="text-right tabular-nums">{p.views}</TableCell>
                          <TableCell className="text-right tabular-nums text-muted-foreground">{p.unique_visitors}</TableCell>
                        </TableRow>
                      ))}
                      {topPages.length === 0 && (
                        <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground">No page data yet.</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Card>
              </div>

              <div>
                <SectionTitle icon={Globe}>Traffic sources</SectionTitle>
                <Card className="p-0 overflow-hidden" testid="an-sources">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Source</TableHead>
                        <TableHead className="text-right">Views</TableHead>
                        <TableHead className="text-right">Unique</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sources.map((s) => (
                        <TableRow key={s.source} data-testid={`an-source-${s.source}`}>
                          <TableCell className="max-w-[220px] truncate font-medium">{s.source}</TableCell>
                          <TableCell className="text-right tabular-nums">{s.views}</TableCell>
                          <TableCell className="text-right tabular-nums text-muted-foreground">{s.unique_visitors}</TableCell>
                        </TableRow>
                      ))}
                      {sources.length === 0 && (
                        <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground">No source data yet.</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Card>
              </div>
            </div>

            {/* REFERRERS + CAMPAIGNS */}
            <div className="grid gap-4 lg:grid-cols-2">
              <div>
                <SectionTitle icon={Link2}>Referring domains</SectionTitle>
                <Card testid="an-referrers">
                  {referrers.length > 0 ? (
                    <div className="space-y-3">
                      {referrers.map((r) => (
                        <DistRow key={r.domain} label={r.domain} count={r.views}
                          total={referrers.reduce((s, x) => s + x.views, 0)} icon={Globe}
                          testid={`an-referrer-${r.domain}`} />
                      ))}
                    </div>
                  ) : <p className="text-sm text-muted-foreground">No external referrers yet (mostly direct traffic).</p>}
                </Card>
              </div>
              <div>
                <SectionTitle icon={Megaphone}>UTM campaigns</SectionTitle>
                <Card testid="an-campaigns">
                  {campaigns.length > 0 ? (
                    <div className="space-y-3">
                      {campaigns.map((cmp) => (
                        <DistRow key={cmp.campaign} label={cmp.campaign} count={cmp.views}
                          total={campaigns.reduce((s, x) => s + x.views, 0)} icon={Megaphone}
                          testid={`an-campaign-${cmp.campaign}`} />
                      ))}
                    </div>
                  ) : <p className="text-sm text-muted-foreground">No campaign-tagged traffic yet (add ?utm_source=... links).</p>}
                </Card>
              </div>
            </div>

            {/* DEVICES + BROWSERS */}
            <div className="grid gap-4 lg:grid-cols-2">
              <div>
                <SectionTitle icon={Monitor}>Devices</SectionTitle>
                <Card testid="an-devices">
                  <div className="space-y-3">
                    <DistRow label="Desktop" count={devices.desktop || 0} total={deviceTotal} icon={Monitor} testid="an-device-desktop" />
                    <DistRow label="Mobile" count={devices.mobile || 0} total={deviceTotal} icon={Smartphone} testid="an-device-mobile" />
                    <DistRow label="Tablet" count={devices.tablet || 0} total={deviceTotal} icon={Tablet} testid="an-device-tablet" />
                    {(devices.unknown || 0) > 0 && (
                      <DistRow label="Unknown" count={devices.unknown || 0} total={deviceTotal} icon={Globe} testid="an-device-unknown" />
                    )}
                  </div>
                </Card>
              </div>
              <div>
                <SectionTitle icon={Globe}>Browsers</SectionTitle>
                <Card testid="an-browsers">
                  {browsers.length > 0 ? (
                    <div className="space-y-3">
                      {browsers.map((b) => (
                        <DistRow key={b.name} label={b.name} count={b.count} total={browserTotal} icon={Globe}
                          testid={`an-browser-${b.name}`} />
                      ))}
                    </div>
                  ) : <p className="text-sm text-muted-foreground">No browser data yet.</p>}
                </Card>
              </div>
            </div>

            {/* USERS + CONVERSION SUMMARY */}
            <SectionTitle icon={UserPlus}>Users &amp; conversion</SectionTitle>
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <StatCard icon={UserPlus} label="Signups" value={u.signups} testid="an-signups" />
              <StatCard icon={Users} label="Free users" value={u.free} accent="text-muted-foreground" testid="an-free" />
              <StatCard icon={BadgeCheck} label="Pro Host users" value={u.pro} accent="text-[hsl(var(--risk-low))]" testid="an-pro" />
              <StatCard icon={BadgeCheck} label="Property Manager users" value={u.pm} testid="an-pm" />
              <StatCard icon={Percent} label="Visitor → Signup" value={`${c.visitor_to_signup}%`} sub="unique visitors who signed up" testid="an-conv-visitor-signup" />
              <StatCard icon={Percent} label="Signup → Paid" value={`${c.signup_to_paid}%`} accent="text-[hsl(var(--risk-low))]" sub="signups on a paid plan" testid="an-conv-signup-paid" />
              <StatCard icon={Percent} label="Revenue per visitor" value={`$${c.revenue_per_visitor ?? 0}`} sub="MRR ÷ public visitors" testid="an-conv-rpv" />
              <StatCard icon={FileText} label="Assessments started" value={a.started} testid="an-ass-started" />
              <StatCard icon={CheckCircle2} label="Assessments completed" value={a.completed} accent="text-[hsl(var(--risk-low))]" testid="an-ass-completed" />
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}
